import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage, useApi } from "@/lib/api";
import { fbEvent } from "@/lib/consent";
import { Btn } from "@/components/site/kit";

const EMPTY = { name: "", phone: "", email: "", service: "", message: "", website: "" };

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Please enter your name";
  if (!/^\+?[0-9\s-]{7,20}$/.test(f.phone.trim())) e.phone = "Enter a valid phone number";
  if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Enter a valid email";
  return e;
};

export const LeadForm = ({ source = "website", product = "", showService = true, submitLabel = "Get Free Consultation", testPrefix = "lead-form", successMessage }) => {
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { data: services } = useApi("/services", showService);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await api.post("/leads", { ...f, email: f.email || null, product, source, page_url: window.location.href });
      fbEvent("Lead", { content_name: product || f.service || source });
      toast.success(successMessage || "Thank you! Our team will contact you shortly.");
      setDone(true);
      setF(EMPTY);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div className="flex flex-col items-center py-10 text-center" data-testid={`${testPrefix}-success`}>
        <CheckCircle2 className="h-14 w-14 text-success" />
        <h3 className="mt-4 text-xl font-bold">Request received!</h3>
        <p className="mt-2 max-w-sm text-sm text-body">{successMessage || "We'll get back to you within one working day. For a faster reply, message us on WhatsApp."}</p>
        <button className="mt-5 text-sm font-semibold text-ocean hover:underline" onClick={() => setDone(false)} data-testid={`${testPrefix}-again-btn`}>Send another request</button>
      </div>
    );

  const field = (k, label, props = {}) => (
    <div>
      <label htmlFor={`${testPrefix}-${k}`} className="label-base">{label}</label>
      <input id={`${testPrefix}-${k}`} className="input-base" value={f[k]} onChange={set(k)} data-testid={`${testPrefix}-${k}-input`} {...props} />
      {errors[k] && <p className="mt-1 text-xs font-medium text-red-600" data-testid={`${testPrefix}-${k}-error`}>{errors[k]}</p>}
    </div>
  );

  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid={testPrefix}>
      <div className="grid gap-4 sm:grid-cols-2">
        {field("name", "Full name *", { placeholder: "Your name", autoComplete: "name" })}
        {field("phone", "Phone / WhatsApp *", { placeholder: "+91 98765 43210", type: "tel", autoComplete: "tel" })}
      </div>
      {field("email", "Email", { placeholder: "you@company.com", type: "email", autoComplete: "email" })}
      {showService && (
        <div>
          <span className="label-base">Service interested in</span>
          <Select value={f.service} onValueChange={set("service")}>
            <SelectTrigger className="h-[46px] rounded-xl border-line bg-white" data-testid={`${testPrefix}-service-select`}>
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {(services || []).map((s) => <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>)}
              <SelectItem value="Ready-made Product">Ready-made Product</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <label htmlFor={`${testPrefix}-message`} className="label-base">Message</label>
        <textarea id={`${testPrefix}-message`} rows={4} className="input-base resize-none" placeholder="Tell us briefly about your requirement" value={f.message} onChange={set("message")} data-testid={`${testPrefix}-message-input`} />
      </div>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="absolute left-[-9999px] h-0 w-0 opacity-0" aria-hidden="true" />
      <Btn type="submit" disabled={loading} className="w-full sm:w-auto" testId={`${testPrefix}-submit-btn`} arrow={!loading}>
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        {loading ? "Sending..." : submitLabel}
      </Btn>
      <p className="text-xs text-muted2">By submitting, you agree to our <a href="/privacy-policy" className="font-semibold text-ocean hover:underline">Privacy Policy</a>. We never share your details.</p>
    </form>
  );
};
